import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { isEmpty, isString } from 'lodash';

import { App, Button, Card, Select, Spin, Table } from 'antd';

import { addDashesToNumber } from '../../../../../shared/utils';
import * as actions from '../../../store/actions';
import { AppDispatch, RootState } from '../../../../../store';
import { AllInsuredNumbersFilter, GetAllInsuredNumbersRequest, InsuredPhoneNumber } from '../../../models';

import './styles.less';

const PAGE_SIZE = 10;
const MAX_SELECTED_NUMBERS = 50;

export type InsuredNumbersSelectorProps = {
  filter: AllInsuredNumbersFilter;
  setSelectedPhoneNumbersAndNames: React.Dispatch<
    React.SetStateAction<
      {
        phoneNumber: string;
        name: string;
        truckingCompanyId?: string;
      }[]
    >
  >;
  selectedPhoneNumbersAndNames: {
    phoneNumber: string;
    name: string;
    truckingCompanyId?: string;
  }[];
};

const InsuredNumbersSelector = ({ filter, setSelectedPhoneNumbersAndNames, selectedPhoneNumbersAndNames }: InsuredNumbersSelectorProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const { modal } = App.useApp();

  const [pageNumber, setPageNumber] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [localAllInsuredNumbers, setLocalAllInsuredNumbers] = useState<InsuredPhoneNumber[]>([]);

  const allInsuredNumbers = useSelector((state: RootState) => state.smses.allInsuredNumbers);
  const allInsuredNumbersLoading = useSelector((state: RootState) => state.smses.allInsuredNumbersLoading);
  const checkPhoneNumberLoading = useSelector((state: RootState) => state.smses.checkPhoneNumberLoading);

  useEffect(() => {
    if (filter) {
      setPageNumber(1);
      setLocalAllInsuredNumbers([]);
      getAllInsuredNumbers(1);
    }
  }, [filter]);

  useEffect(() => {
    let newArray = localAllInsuredNumbers?.slice();

    if (newArray && newArray.length > 0) {
      newArray.push(...(allInsuredNumbers?.insuredPhoneNumbers ?? []));
    } else {
      newArray = allInsuredNumbers?.insuredPhoneNumbers ?? [];
    }

    setHasMore(allInsuredNumbers?.hasMore ?? true);
    setLocalAllInsuredNumbers(newArray);
  }, [allInsuredNumbers]);

  const getAllInsuredNumbers = useCallback(
    (pageNumber: number) => {
      dispatch(
        actions.getAllInsuredNumbers({
          filter,
          pageNumber,
          pageSize: PAGE_SIZE,
        } as GetAllInsuredNumbersRequest),
      );
    },
    [filter],
  );

  const getTableColumns = () => {
    return [
      {
        title: 'E-Mail',
        dataIndex: 'eMail',
        key: 'eMail',
        sorter: false,
        render: (text: string) => (
          <span title={text} className="text-ellipsis-150">
            {text}
          </span>
        ),
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        sorter: false,
        render: (text: string) => (
          <span title={text} className="text-ellipsis-150">
            {text}
          </span>
        ),
      },
      {
        title: 'SMS Number',
        dataIndex: 'smsPhone',
        key: 'smsPhone',
        sorter: false,
        render: (text: string, record: InsuredPhoneNumber) => {
          return text ? (
            <Button
              type="primary"
              size="small"
              style={{ fontSize: '11px', padding: '2px 6px' }}
              onClick={() => onAdd(record.name, addDashesToNumber(text), record.id)}
            >
              {addDashesToNumber(text)}
            </Button>
          ) : undefined;
        },
      },
      {
        title: 'Cell Number',
        dataIndex: 'cellPhone',
        key: 'cellPhone',
        sorter: false,
        render: (text: string, record: InsuredPhoneNumber) => {
          return text ? (
            <Button
              type="primary"
              size="small"
              style={{ fontSize: '11px', padding: '2px 6px' }}
              onClick={() => onAdd(record.name, addDashesToNumber(text), record.id)}
            >
              {addDashesToNumber(text)}
            </Button>
          ) : undefined;
        },
      },
    ];
  };

  const loadMoreData = useCallback(() => {
    const newPageNumber = pageNumber + 1;
    setPageNumber(newPageNumber);
    getAllInsuredNumbers(newPageNumber);
  }, [pageNumber, getAllInsuredNumbers]);

  const onSelectScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;

      const maxScroll = target.scrollHeight - target.clientHeight;
      const atTheEnd = target.scrollTop + 50 >= maxScroll;

      if (atTheEnd && hasMore === true && !allInsuredNumbersLoading) {
        loadMoreData();
      }
    },
    [hasMore, allInsuredNumbersLoading, loadMoreData],
  );

  const onChange = (selectValue: string[]) => {
    let newValue = selectValue[selectValue.length - 1];

    if (newValue === undefined || newValue === null || newValue === '') {
      setSelectedPhoneNumbersAndNames([]);
    } else {
      dispatch(actions.checkPhoneNumber({ phoneNumber: newValue })).then((result) => {
        if (isString(result?.errorMessage) && !isEmpty(result?.errorMessage)) {
          modal.warning({
            title: 'Phone number',
            content: result.errorMessage,
          });
        } else {
          let isNew = !selectedPhoneNumbersAndNames.some((x) => x.phoneNumber === result.validPhoneNumber);
          if (isNew) {
            setSelectedPhoneNumbersAndNames([...selectedPhoneNumbersAndNames, { phoneNumber: result.validPhoneNumber, name: '---' }]);
          } else {
            setSelectedPhoneNumbersAndNames(selectedPhoneNumbersAndNames.filter((x) => selectValue.includes(x.phoneNumber)));
          }
        }
      });
    }
  };

  const onAdd = (name: string, phoneNumber: string, truckingCompanyId: string) => {
    if (selectedPhoneNumbersAndNames.length > MAX_SELECTED_NUMBERS) {
      modal.warning({
        title: 'Max count of numbers',
        content: 'You have selected your maximum count of numbers.',
      });

      return;
    }

    dispatch(actions.checkPhoneNumber({ phoneNumber })).then((result) => {
      if (isString(result?.errorMessage) && !isEmpty(result?.errorMessage)) {
        modal.warning({
          title: 'Phone number',
          content: result?.errorMessage,
        });
      } else {
        if (selectedPhoneNumbersAndNames.filter((number) => number.phoneNumber === result.validPhoneNumber).length > 0) {
          modal.warning({
            title: 'Phone number',
            content: `${result.validPhoneNumber} already exist in recipients list.`,
          });

          return;
        }

        setSelectedPhoneNumbersAndNames([...selectedPhoneNumbersAndNames, { name, phoneNumber: result.validPhoneNumber, truckingCompanyId }]);
      }
    });
  };

  return (
    <>
      <Card className="insured-numbers-selector-panel" bordered={true}>
        <div onScrollCapture={onSelectScroll}>
          <Table
            id="insured-phone-numbers-table"
            className="insured-phone-numbers-table"
            size="small"
            scroll={{ y: 355 }}
            loading={allInsuredNumbersLoading}
            columns={getTableColumns()}
            rowKey={(record) => record.id}
            dataSource={localAllInsuredNumbers}
            pagination={false}
          />
        </div>
      </Card>

      <Spin spinning={checkPhoneNumberLoading}>
        <Select
          mode="tags"
          style={{ width: '100%' }}
          placeholder="Phone numbers"
          open={false}
          value={selectedPhoneNumbersAndNames.map((x) => x.phoneNumber)}
          onChange={onChange}
          allowClear
        />
      </Spin>
    </>
  );
};

export default InsuredNumbersSelector;
